/**
 * Page minimale pour finaliser un paiement Stripe (client_secret dans le hash).
 * Utilisée par l'app mobile : ouvre cette URL avec #secret=pi_xxx_secret_xxx
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaCreditCard } from 'react-icons/fa';
import { getStripePublishableKey } from '../config/stripe';

const stripePromise = getStripePublishableKey()
  ? loadStripe(getStripePublishableKey())
  : null;

function CheckoutPayForm({ clientSecret, onSuccess, onError }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setMessage('');
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/order-confirmation?mobile=1`,
        },
      });
      if (error) {
        setMessage(error.message || t('checkoutPay.genericError'));
        onError?.(error);
        return;
      }
      onSuccess?.();
    } catch (err) {
      setMessage(err.message || t('checkoutPay.paymentError'));
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  if (!clientSecret) {
    return (
      <div className="p-6 text-center text-gray-900">
        <p>{t('checkoutPay.invalidLink')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto p-6">
      <PaymentElement
        options={{
          layout: 'tabs',
          defaultCollapsed: false,
        }}
      />
      {message && (
        <div className="form-message form-message--error text-sm">
          {message}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || loading}
        className="cart-page-cta w-full font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          t('checkoutPay.processing')
        ) : (
          <>
            <FaCreditCard className="shrink-0" size={18} aria-hidden />
            {t('checkoutPay.pay')}
          </>
        )}
      </button>
    </form>
  );
}

const CheckoutPay = () => {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState('');
  const [stripeReady, setStripeReady] = useState(false);
  const [success, setSuccess] = useState(false);
  const parsedRef = useRef(false);

  useEffect(() => {
    if (parsedRef.current) return;
    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);
    const secret = params.get('secret') || params.get('client_secret');
    if (secret) {
      setClientSecret(secret);
    }
    parsedRef.current = true;
  }, []);

  useEffect(() => {
    if (getStripePublishableKey()) {
      setStripeReady(true);
    }
  }, []);

  const handleSuccess = () => {
    setSuccess(true);
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'payment_success' }));
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="text-center">
          <div className="text-5xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-[#054625] mb-2">{t('checkoutPay.paymentSuccess')}</h1>
          <p className="text-gray-900 font-normal">{t('checkoutPay.closeAndReturn')}</p>
        </div>
      </div>
    );
  }

  if (!stripeReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <p className="text-gray-900">{t('checkoutPay.stripeNotConfigured')}</p>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="text-center text-gray-900">
          <p>{t('checkoutPay.noPaymentToMake')}</p>
          <p className="mt-2 text-sm">{t('checkoutPay.useAppLink')}</p>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: { colorPrimary: '#15803d' },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-lg mx-auto px-4">
        <h1 className="text-xl font-bold text-gray-800 mb-2 text-center">{t('checkoutPay.finalizePayment')}</h1>
        <p className="text-gray-900 font-normal text-sm text-center mb-6">{t('checkoutPay.securePaymentByStripe')}</p>
      </div>
      <Elements stripe={stripePromise} options={options}>
        <CheckoutPayForm
          clientSecret={clientSecret}
          onSuccess={handleSuccess}
        />
      </Elements>
    </div>
  );
};

export default CheckoutPay;
